import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, NotePencil, ClipboardText, ArrowRight, EnvelopeSimple, Briefcase, House, UserCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";
import { DispositionModal } from "./DispositionModal";
import { LeadRecordModal } from "../shared/LeadRecordModal";
import { useScriptPanel } from "../../context/ScriptPanelContext";
import { useAuth } from "../../context/AuthContext";
import { useLeadFocus } from "../../context/LeadFocusContext";
const CATEGORIES = [
  { key: "trust", label: "Trust & Asset Protection Leads", shortLabel: "Trust & Asset Protection" },
  { key: "funding", label: "Business Funding Leads", shortLabel: "Business Funding" },
];
const PAGE_SIZE = 50;

function formatPhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone || "No phone on file";
}
function normDigits(s) {
  return (s || "").replace(/\D/g, "");
}
function clean(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") out[k] = v; });
  return Object.keys(out).length ? out : null;
}

// Neo Prompt (Sep 18 2026) — exact field mappings confirmed by Wes against a live production
// lead record. `enrichment` on the lead doc is the untouched DataMoon Enrichment API response:
// { bestMobile, identities: [{ age, birthDate, address, city, state, zip, phones: [{phone,type}],
// companies: [{name, industry, revenue, employeeCount, address}] }], data: { gender,
// maritalStatus, householdIncome, creditRange, homeOwnership, dwellingType, homeValue,
// householdNetWorth, numChildrenHh, numAdultsHh, education } }. No approximated field names.
//
// Fix (Sep 19 2026) — the real Enrichment API call only succeeds when it's actually reachable
// with a valid key; whenever it fails/is unavailable, `enrichment` is stored as null and the
// lead instead carries the older, always-populated `prospect_profile` shape (identity,
// location_detail, household, financial/business) built inline from the raw audience row.
// Every getter below now checks `enrichment` first and falls back to `prospect_profile` field by
// field, so a card never goes blank just because the rich Enrichment API didn't return data.
function getIdentity0(lead) { return lead.enrichment?.identities?.[0] || null; }
function getData(lead) { return lead.enrichment?.data || null; }
function getCompany(lead) { return lead.enrichment?.identities?.[0]?.companies?.[0] || null; }
function getProspectProfile(lead) { return lead.prospect_profile || null; }

// Card type routing: trust -> Trust card. funding + a company on file (enrichment OR legacy
// prospect_profile.business_name) -> Business Funding card. funding + no company -> Personal.
function cardTypeFor(lead) {
  if (lead.source !== "funding") return "trust";
  const pp = getProspectProfile(lead);
  const hasCompany = !!getCompany(lead) || !!pp?.business_name || !!pp?.business?.business_name;
  return hasCompany ? "business_funding" : "personal_funding";
}
const CARD_TYPE_META = {
  trust: { badge: "Trust Lead", badgeClass: "bg-igc-gold/15 text-igc-goldText" },
  business_funding: { badge: "Business Funding", badgeClass: "bg-igc-success/15 text-igc-success" },
  personal_funding: { badge: "Personal Funding", badgeClass: "bg-igc-success/15 text-igc-success" },
};
// New Build 15 (Sep 18 2026) — live funding pipeline status straight from Trulli's webhook.
const TRULLI_STATUS_CLASSES = {
  Received: "bg-igc-gold/10 text-igc-goldText",
  "Under Review": "bg-amber-500/15 text-amber-500",
  Approved: "bg-igc-success/15 text-igc-success",
  Funded: "bg-igc-success/20 text-igc-success",
  Declined: "bg-igc-danger/15 text-igc-danger",
  Unqualified: "bg-igc-danger/10 text-igc-danger",
};

// Phone dropdown — Neo Prompt exact rules: enrichment.bestMobile first, always labeled
// "Mobile · Recommended"; then loop enrichment.identities[0].phones[] (phone + type), mapped to
// human labels, deduplicated against bestMobile. Never a flat single number — always a dropdown,
// even with one entry, so the rep always sees the type label.
const PHONE_TYPE_LABELS = { mobile: "Mobile", business: "Business", home: "Home", personal: "Personal", other: "Other" };
function getPhoneOptions(lead) {
  const e = lead.enrichment;
  if (e && (e.bestMobile || e.identities?.[0]?.phones?.length)) {
    const options = [];
    const seen = new Set();
    if (e.bestMobile) {
      options.push({ number: e.bestMobile, label: "Mobile · Recommended", verified: true });
      seen.add(normDigits(e.bestMobile));
    }
    (e.identities?.[0]?.phones || []).forEach((p) => {
      const digits = normDigits(p?.phone);
      if (!p?.phone || !digits || seen.has(digits)) return;
      seen.add(digits);
      options.push({ number: p.phone, label: PHONE_TYPE_LABELS[(p.type || "other").toLowerCase()] || "Other", verified: false });
    });
    if (options.length > 0) return options;
  }
  // Legacy fallback — leads pulled before this enrichment shape existed
  const legacy = lead.prospect_profile?.phones;
  if (Array.isArray(legacy) && legacy.length > 0) {
    return legacy.map((p) => ({ number: p.number, label: p.recommended ? "Mobile · Recommended" : (p.label || "Other").replace(/^[^\w]*/, ""), verified: !!p.verified }));
  }
  if (lead.phone) return [{ number: lead.phone, label: "Mobile · Recommended", verified: true }];
  return [];
}
function defaultPhoneFor(lead) {
  return getPhoneOptions(lead)[0]?.number || "";
}

// Exact field-mapped section data getters (Neo Prompt) — every value comes straight from
// enrichment.data / enrichment.identities[0] / enrichment.identities[0].companies[0], no guessing.
// Section field getters (Build 15 exact spec, Sep 19 2026) — Contact / Personal Profile /
// Financial & Household / Business Intelligence. Every value comes straight from
// enrichment.data / enrichment.identities[0] / enrichment.identities[0].companies[0], falling
// back to the structured legacy prospect_profile shape field-by-field, and finally to
// prospect_profile.profile_fields (Sep 19 2026 fix, confirmed against Jessica Smith's live
// production record) — a flat [{label, value}] array that is ALL some older leads have, since
// the structured identity/household/financial/business sub-objects were only added to
// build_prospect_profile in the Sep 18 rebuild and don't exist on leads pulled before that.
// Only fields with real data are ever rendered — nothing blank, nothing fabricated.
function formatAddressLine(loc) {
  if (!loc) return null;
  const parts = [];
  const line1 = loc.address || loc.street;
  if (line1) parts.push(line1);
  const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
  const csz = loc.zip ? `${cityState} ${loc.zip}`.trim() : cityState;
  if (csz) parts.push(csz);
  return parts.length ? parts.join(", ") : null;
}
function getProfileFieldsMap(lead) {
  const fields = lead.prospect_profile?.profile_fields;
  const map = {};
  if (Array.isArray(fields)) {
    fields.forEach((f) => { if (f?.label) map[String(f.label).trim().toLowerCase()] = f.value; });
  }
  return map;
}
function pf(map, ...labels) {
  for (const l of labels) {
    const v = map[l.toLowerCase()];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}
function getPersonalProfile(lead) {
  const idn = getIdentity0(lead);
  const d = getData(lead);
  const pp = getProspectProfile(lead);
  const fm = getProfileFieldsMap(lead);
  const address = formatAddressLine(idn) || formatAddressLine(pp?.location_detail) || pp?.location;
  if (cardTypeFor(lead) === "business_funding") {
    return clean({
      age: idn?.age ?? pp?.identity?.age_range ?? pf(fm, "Age Range"),
      gender: d?.gender ?? pp?.identity?.gender,
      address,
    });
  }
  return clean({
    age: idn?.age ?? pp?.identity?.age_range ?? pf(fm, "Age Range"),
    date_of_birth: idn?.birthDate ?? pp?.identity?.date_of_birth,
    gender: d?.gender ?? pp?.identity?.gender,
    marital_status: d?.maritalStatus ?? pp?.household?.marital_status ?? pf(fm, "Marital Status"),
    education: d?.education ?? pp?.household?.education_level,
    address,
  });
}
function getBusinessIntel(lead) {
  const c = getCompany(lead);
  const pp = getProspectProfile(lead);
  const fm = getProfileFieldsMap(lead);
  return clean({
    company_name: c?.name ?? pp?.business?.business_name ?? pp?.business_name,
    industry: c?.industry ?? pp?.business?.industry ?? pf(fm, "Industry / Type of Business"),
    annual_revenue: c?.revenue ?? pp?.business?.annual_revenue ?? pf(fm, "Annual Revenue", "Estimated Monthly Revenue"),
    employee_count: c?.employeeCount ?? pp?.business?.employees ?? pf(fm, "Number of Employees"),
    company_address: c?.address ?? formatAddressLine(pp?.location_detail) ?? pp?.location,
  });
}
function getFinancialHousehold(lead) {
  const d = getData(lead);
  const pp = getProspectProfile(lead);
  const fm = getProfileFieldsMap(lead);
  if (cardTypeFor(lead) === "business_funding") {
    return clean({
      household_income: d?.householdIncome,
      credit_range: d?.creditRange ?? pp?.business?.credit_range,
      household_net_worth: d?.householdNetWorth ?? pp?.business?.estimated_net_worth,
    });
  }
  return clean({
    household_income: d?.householdIncome ?? pp?.financial?.household_income ?? pf(fm, "Income Range"),
    credit_range: d?.creditRange ?? pp?.financial?.credit_range,
    home_ownership: d?.homeOwnership ?? pp?.financial?.homeownership_status ?? pf(fm, "Homeowner Status"),
    dwelling_type: d?.dwellingType ?? pp?.financial?.dwelling_type,
    home_value: d?.homeValue ?? pp?.financial?.home_value,
    household_net_worth: d?.householdNetWorth ?? pf(fm, "Estimated Net Worth"),
    adults_in_home: d?.numAdultsHh ?? pp?.household?.adults_in_household,
    children_in_home: d?.numChildrenHh ?? pp?.household?.children_in_household,
  });
}

const FIELD_LABELS = {
  age: "Age", date_of_birth: "Date of Birth", gender: "Gender", marital_status: "Marital Status",
  education: "Education", address: "Address",
  household_income: "Household Income", credit_range: "Est. Credit Range", home_ownership: "Home Ownership",
  dwelling_type: "Dwelling Type", home_value: "Home Value", household_net_worth: "Household Net Worth",
  adults_in_home: "Adults in Home", children_in_home: "Children in Home",
  company_name: "Company Name", industry: "Industry", annual_revenue: "Annual Revenue",
  employee_count: "Employee Count", company_address: "Company Address",
};

function SectionBox({ icon, label, children, testid }) {
  return (
    <div className="bg-igc-bg/60 border border-igc-gold/10 rounded-lg p-4 h-full" data-testid={testid}>
      <p className="text-igc-muted text-[10px] uppercase tracking-widest font-medium mb-3 flex items-center gap-1.5">{icon}{label}</p>
      {children}
    </div>
  );
}

function LeadCardSkeleton() {
  return (
    <div className="bg-igc-surface border border-igc-gold/15 p-6 lg:p-7 animate-pulse" data-testid="rep-lead-skeleton">
      <div className="pb-5 border-b border-igc-gold/10">
        <div className="h-4 w-24 bg-igc-surfaceLight rounded-full mb-3" />
        <div className="h-7 w-2/3 bg-igc-surfaceLight rounded mb-2" />
        <div className="h-3 w-1/3 bg-igc-surfaceLight rounded" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-5">
        <div className="h-20 bg-igc-surfaceLight rounded-lg" />
        <div className="h-20 bg-igc-surfaceLight rounded-lg" />
      </div>
    </div>
  );
}

function FieldGrid({ data, testidPrefix }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <p className="text-igc-muted text-xs">No data on file for this section.</p>;
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {entries.map(([key, value]) => (
        <div key={key} data-testid={`${testidPrefix}-${key}`}>
          <p className="text-igc-muted text-[9px] uppercase tracking-widest">{FIELD_LABELS[key] || key}</p>
          <p className="text-igc-cream text-sm font-semibold">{String(value)}</p>
        </div>
      ))}
    </div>
  );
}

function PhoneDropdown({ lead, value, onChange }) {
  const options = getPhoneOptions(lead);
  if (options.length === 0) return <span className="text-igc-muted text-xs" data-testid={`rep-lead-no-phone-${lead.id}`}>No phone on file</span>;
  return (
    <select
      data-testid={`rep-lead-phone-select-${lead.id}`}
      value={value || ""}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
      title="Choose which phone number on file to dial for this lead. 'Recommended' is our best guess at the most reliable number to reach them — the others are alternates on file if that one doesn't work."
      className="bg-igc-bg border border-igc-gold/25 text-igc-success text-sm px-3 py-2 rounded focus:outline-none focus:border-igc-gold cursor-pointer w-full"
    >
      {options.map((p, idx) => (
        <option key={`${p.number}-${idx}`} value={p.number}>{p.label} — {formatPhone(p.number)}</option>
      ))}
    </select>
  );
}

function LeadCard({ lead: l, selectedPhone, onSelectPhone, onCall, calling, onLogDisposition, onOpenScripts, onOpenNotes, onFocus, isResumeMarker }) {
  const daysOld = Math.max(0, Math.floor((Date.now() - new Date(l.created_at).getTime()) / 86400000));
  const type = cardTypeFor(l);
  const meta = CARD_TYPE_META[type];
  const isBusiness = type === "business_funding";
  const company = isBusiness ? getCompany(l) : null;
  const headline = isBusiness ? (company?.name || l.name) : l.name;
  const personalProfile = getPersonalProfile(l);
  const businessIntel = isBusiness ? getBusinessIntel(l) : null;
  const financialHousehold = getFinancialHousehold(l);
  const sectionCount = [personalProfile, businessIntel, financialHousehold].filter(Boolean).length;

  return (
    <div>
      {isResumeMarker && (
        <p className="text-igc-goldText text-xs font-medium flex items-center gap-1.5 mb-1 pl-1" data-testid={`rep-lead-resume-marker-${l.id}`} title="This marker remembers the last lead you were working on before you left or took a break. When you come back, it highlights exactly where to pick up so you don't lose your place in the list or accidentally skip leads. Click into this lead and keep going.">
          <ArrowRight size={13} weight="bold" /> Resume Here
        </p>
      )}
      <div
        onClick={onFocus}
        className={`bg-igc-surface border p-6 lg:p-7 cursor-pointer ${isResumeMarker ? "border-igc-gold" : "border-igc-gold/15"}`}
        data-testid={`rep-assigned-lead-${l.id}`}
        data-card-type={type}
      >
        {/* Header — Identity */}
        <div className="pb-5 border-b border-igc-gold/10">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`text-[10px] px-2 py-0.5 tracking-wide font-medium rounded-full ${meta.badgeClass}`} data-testid={`rep-lead-type-badge-${l.id}`} title="What kind of lead this is — Trust Lead, Business Funding, or Personal Funding. This determines which script and fields you see on this card.">{meta.badge}</span>
            <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-gold/10 text-igc-goldText" data-testid={`rep-lead-status-badge-${l.id}`} title="Where this lead is in your pipeline: New means you haven't logged a disposition yet, Contacted means you have, Closed/Dead means the deal is finished one way or another.">{l.stage}</span>
            {l.trulli_status && (
              <span
                className={`text-[10px] px-2 py-1 tracking-wide font-medium rounded-full ${TRULLI_STATUS_CLASSES[l.trulli_status] || "bg-igc-gold/10 text-igc-goldText"}`}
                data-testid={`rep-lead-trulli-status-${l.id}`}
                title={l.trulli_status_reason ? `Funding pipeline status: ${l.trulli_status} — ${l.trulli_status_reason}` : `Funding pipeline status: ${l.trulli_status}`}
              >
                {l.trulli_status}
              </span>
            )}
          </div>
          <p className="font-serif text-3xl text-igc-cream leading-tight break-words" data-testid={`rep-lead-name-${l.id}`} title="The prospect's name (or business name for Business Funding leads) — this is who you'll be calling.">{headline}</p>
          {isBusiness && l.name && (
            <p className="text-igc-muted text-sm mt-1" data-testid={`rep-lead-owner-name-${l.id}`} title="The individual owner of this business — the one you'll actually be speaking with on the call.">Owner: {l.name}</p>
          )}
          <div className="flex items-center gap-4 flex-wrap mt-2.5 text-igc-muted text-[11px]">
            <span data-testid={`rep-lead-assigned-date-${l.id}`} title="The date this lead was assigned to your queue, and how many days it's been sitting there. Older leads should generally be worked first.">Assigned {new Date(l.created_at).toLocaleDateString()} ({daysOld === 0 ? "New today" : `${daysOld}d old`})</span>
            <span className="text-igc-goldText/80" data-testid={`rep-lead-call-count-${l.id}`} title="How many times you've clicked Call on this specific lead. Every click adds 1, even if it's the same lead — this helps you see at a glance whether you've followed up enough.">{l.call_count > 0 ? `Called ${l.call_count}x` : "Never called"}</span>
          </div>
        </div>

        {/* Contact — always right after identity, per spec */}
        <div className="py-5 border-b border-igc-gold/10">
          <SectionBox icon={<Phone size={11} />} label="Contact" testid={`rep-lead-contact-box-${l.id}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <PhoneDropdown lead={l} value={selectedPhone} onChange={onSelectPhone} />
                <Button
                  data-testid={`rep-lead-call-button-${l.id}`}
                  onClick={(e) => { e.stopPropagation(); onCall(l); }}
                  disabled={calling}
                  title="Dials the phone number selected in the dropdown above, logs this as a call attempt, starts Nate's automatic follow-up email sequence if a verified email is on file, and then opens a required box for you to log what happened on the call."
                  className="h-10 text-sm bg-igc-gold hover:bg-igc-goldHover text-black font-semibold flex items-center justify-center gap-1.5 w-full"
                >
                  <Phone size={15} weight="fill" /> {calling ? "Logging…" : "Call"}
                </Button>
                <p className="text-igc-muted text-[10px] leading-snug" data-testid={`rep-lead-call-explainer-${l.id}`}>
                  Nate's sequence starts the moment you call — you'll receive a copy of every email sent.
                </p>
              </div>
              <div className="flex flex-col justify-center gap-1.5">
                <p className="text-igc-cream text-sm font-semibold" data-testid={`rep-lead-contact-name-${l.id}`}>{l.name}</p>
                {l.email ? (
                  <p className="text-igc-cream text-xs flex items-center gap-1.5 min-w-0" data-testid={`rep-lead-email-${l.id}`} title="This prospect's verified email address on file — Nate's automatic follow-up emails are sent here.">
                    <EnvelopeSimple size={12} className="text-igc-muted shrink-0" /> <span className="truncate">{l.email}</span>
                  </p>
                ) : (
                  <p className="text-igc-danger text-xs flex items-center gap-1.5" data-testid={`rep-lead-nate-skipped-${l.id}`} title="Nate's automatic follow-up email sequence only fires when a verified email is on file. This lead has none, so Nate did not fire — call them directly instead.">
                    <EnvelopeSimple size={12} className="shrink-0" /> No verified email — Nate could not fire
                  </p>
                )}
              </div>
            </div>
          </SectionBox>
        </div>

        {/* Personal Profile / Business Intelligence / Financial & Household */}
        {sectionCount > 0 && (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${sectionCount >= 3 ? "lg:grid-cols-3" : ""} gap-4 py-5 border-b border-igc-gold/10`}>
            {personalProfile && (
              <SectionBox icon={<UserCircle size={11} />} label="Personal Profile" testid={`rep-lead-personal-box-${l.id}`}>
                <FieldGrid data={personalProfile} testidPrefix={`rep-lead-personal-${l.id}`} />
              </SectionBox>
            )}
            {businessIntel && (
              <SectionBox icon={<Briefcase size={11} />} label="Business Intelligence" testid={`rep-lead-businessintel-box-${l.id}`}>
                <FieldGrid data={businessIntel} testidPrefix={`rep-lead-businessintel-${l.id}`} />
              </SectionBox>
            )}
            {financialHousehold && (
              <SectionBox icon={<House size={11} />} label="Financial & Household" testid={`rep-lead-financial-box-${l.id}`}>
                <FieldGrid data={financialHousehold} testidPrefix={`rep-lead-financial-${l.id}`} />
              </SectionBox>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="pt-5 flex items-center gap-2 flex-wrap">
          <button
            data-testid={`rep-lead-log-call-${l.id}`}
            onClick={(e) => { e.stopPropagation(); onLogDisposition(); }}
            title="Opens the box where you record what happened on a call with this lead — result and notes — without actually placing a new call. Use this if you called from your own phone instead of clicking Call here."
            className="flex items-center gap-1 text-[11px] text-igc-goldText/80 hover:text-igc-goldText border border-igc-gold/30 px-2.5 py-1.5 rounded-full transition-colors"
          >
            <Phone size={11} weight="duotone" /> Log Disposition
          </button>
          <button
            data-testid={`rep-lead-scripts-button-${l.id}`}
            onClick={(e) => { e.stopPropagation(); onOpenScripts(); }}
            title="Opens the call script panel with word-for-word talking points for this type of lead, plus a direct Call button, so you can read the script while dialing."
            className="flex items-center gap-1 text-[11px] text-igc-goldText/80 hover:text-igc-goldText border border-igc-gold/30 px-2.5 py-1.5 rounded-full transition-colors"
          >
            <NotePencil size={11} weight="duotone" /> Scripts
          </button>
          <button
            data-testid={`rep-lead-notes-button-${l.id}`}
            onClick={(e) => { e.stopPropagation(); onOpenNotes(); }}
            title="Opens this lead's full record — every note anyone has ever left, plus a box to add your own. Notes save immediately and everyone who touches this lead can see them."
            className="flex items-center gap-1 text-[11px] text-igc-goldText/80 hover:text-igc-goldText border border-igc-gold/30 px-2.5 py-1.5 rounded-full transition-colors"
          >
            <ClipboardText size={11} weight="duotone" /> Notes
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryLeadList({ category, label }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [callingId, setCallingId] = useState(null);
  const [dispositionLead, setDispositionLead] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [selectedPhones, setSelectedPhones] = useState({});
  const [nameFilter, setNameFilter] = useState("");
  const scriptPanel = useScriptPanel();
  const { user } = useAuth();
  const { setFocusedLead } = useLeadFocus();

  useEffect(() => {
    setLoading(true);
    api.get(`/rep/assigned-leads?category=${category}&page=${page}&page_size=${PAGE_SIZE}`)
      .then(({ data }) => {
        setLeads(data.leads);
        setTotal(data.total);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [category, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Phone/email show immediately on every card — no reveal-click gate (Bug 1). Clicking Call
  // still logs a call-attempt (Calls Attempted stat) in the background, then opens the
  // required disposition modal. The lead is never removed from this list (Bug 2) — only a
  // saved disposition changes its status.
  // Master Prompt (Wes, Sep 17 2026): the number the rep picked from the dropdown is what
  // actually gets dialed and logged — never silently falls back to a different number.
  // NATE-R sequences fire automatically the instant this endpoint logs the first CALL click —
  // no separate confirmation, no extra step (Neo Prompt, Sep 18 2026), the rep gets a copy email.
  const callLead = async (lead) => {
    const phoneNumber = selectedPhones[lead.id] || defaultPhoneFor(lead);
    setCallingId(lead.id);
    api.post(`/rep/leads/${lead.id}/reveal-phone`, { portal_section: "leads_tab", phone_number: phoneNumber }).catch(() => {}).finally(() => setCallingId(null));
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, call_count: (l.call_count || 0) + 1 } : l)));
    window.dispatchEvent(new Event("igc:call-logged"));
    setDispositionLead({ ...lead, selected_phone: phoneNumber, via_call_button: true });
  };

  const [openedIds, setOpenedIds] = useState(new Set());
  const markOpened = (lead) => {
    setFocusedLead(lead);
    if (openedIds.has(lead.id)) return;
    setOpenedIds((prev) => new Set(prev).add(lead.id));
    api.post(`/rep/leads/${lead.id}/open`).catch(() => {});
  };

  const visibleLeads = nameFilter.trim()
    ? leads.filter((l) => (l.name || "").toLowerCase().includes(nameFilter.trim().toLowerCase()))
    : leads;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg text-igc-cream tracking-tight" title="Every lead assigned to you in this category, newest work first — scroll through and work them one by one.">{label}</h3>
        {total > 0 && <span className="text-igc-muted text-xs" data-testid={`rep-leads-total-${category}`} title="The total number of leads assigned to you in this category, across every page.">{total.toLocaleString()} lead{total === 1 ? "" : "s"}</span>}
      </div>
      <div className="relative mb-3 max-w-xs">
        <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
        <input
          data-testid={`rep-leads-name-filter-${category}`}
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Find a client by name..."
          title="Type a client's name to instantly filter the cards below on this page — handy while you're on a call and need to find someone fast. This only searches your own leads on this page, it doesn't navigate anywhere."
          className="w-full bg-igc-surfaceLight border border-igc-gold/20 text-igc-cream text-sm pl-8 pr-3 py-2 focus:outline-none focus:border-igc-gold"
        />
      </div>
      <div className="space-y-4">
        {loading && (
          <>
            <LeadCardSkeleton />
            <LeadCardSkeleton />
          </>
        )}
        {!loading && leads.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid={`rep-leads-empty-${category}`}>
            No leads assigned here yet — your manager or admin will load these into your folder as they come in.
          </p>
        )}
        {!loading && leads.length > 0 && visibleLeads.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid={`rep-leads-name-filter-empty-${category}`}>No leads on this page match "{nameFilter}".</p>
        )}
        {!loading && visibleLeads.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            isResumeMarker={l.is_resume_marker}
            selectedPhone={selectedPhones[l.id] || defaultPhoneFor(l)}
            onSelectPhone={(num) => setSelectedPhones((prev) => ({ ...prev, [l.id]: num }))}
            onCall={callLead}
            calling={callingId === l.id}
            onFocus={() => markOpened(l)}
            onLogDisposition={() => setDispositionLead({ ...l, selected_phone: selectedPhones[l.id] || defaultPhoneFor(l), via_call_button: false })}
            onOpenScripts={() => scriptPanel?.openScriptPanel(l, category)}
            onOpenNotes={() => setNotesLead(l)}
          />
        ))}
      </div>
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3">
          <Button data-testid={`rep-leads-prev-${category}`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} variant="outline" size="sm" title="Goes back to the previous page of leads in this category." className="border-igc-gold/30 text-igc-goldText">Previous</Button>
          <span className="text-igc-muted text-xs">Page {page} of {totalPages}</span>
          <Button data-testid={`rep-leads-next-${category}`} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} variant="outline" size="sm" title="Moves forward to the next page of leads in this category." className="border-igc-gold/30 text-igc-goldText">Next</Button>
        </div>
      )}
      {dispositionLead && (
        <DispositionModal lead={dispositionLead} onComplete={() => setDispositionLead(null)} onClose={() => setDispositionLead(null)} />
      )}
      {notesLead && (
        <LeadRecordModal lead={notesLead} currentUserId={user?.id} userRole={user?.role} onClose={() => setNotesLead(null)} />
      )}
    </div>
  );
}

// Filter toggle (Sep 19 2026) — trust and funding leads used to render stacked on the same
// page, forcing a rep to scroll past all 36 trust leads to reach the 50 funding ones below.
// Now only one category renders at a time; the `key` swap below forces CategoryLeadList to
// remount cleanly (fresh page 1, cleared name filter) the instant a rep switches.
export function RepLeadsTab() {
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].key);
  const activeCat = CATEGORIES.find((c) => c.key === activeCategory) || CATEGORIES[0];
  return (
    <div data-testid="rep-leads-tab" className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap" data-testid="rep-leads-category-toggle">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            data-testid={`rep-leads-category-button-${cat.key}`}
            onClick={() => setActiveCategory(cat.key)}
            title={`Show only your ${cat.shortLabel} leads.`}
            className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors ${
              activeCategory === cat.key
                ? "bg-igc-gold text-black border-igc-gold"
                : "border-igc-gold/30 text-igc-goldText hover:bg-igc-gold/10"
            }`}
          >
            {cat.shortLabel}
          </button>
        ))}
      </div>
      <CategoryLeadList key={activeCat.key} category={activeCat.key} label={activeCat.label} />
    </div>
  );
}
